"use client";

import React, { useCallback, useId, useMemo, useRef, useState } from "react";
import { useFilter } from "react-aria";
import { twMerge } from "tailwind-merge";
import { Button } from "@/components/Button";
import { CheckboxIndicator } from "@/components/Checkbox/Checkbox";
import { Chip } from "@/components/Chip/Chip";
import { Description, FieldError, getInputBaseStyles, InputWrapper, Label, useInputFocus } from "@/components/Field";
import { Icon } from "@/components/Icon";
import { Loader } from "@/components/Loader";
import { controlStyles, type Size } from "@/utils/controlStyles";
import {
  filterMultiSelectOptions,
  type MultiSelectOption,
  multiSelectSelectionEntries,
  shouldOfferLiteralOption,
} from "./multiSelectFilter";

export interface MultiSelectProps {
  options: readonly MultiSelectOption[];
  /** Controlled selection. Order is insertion order. */
  value?: readonly string[];
  /** Uncontrolled initial selection. */
  defaultValue?: readonly string[];
  onChange: (next: string[]) => void;
  label: string;
  placeholder?: string;
  size?: Size;
  loading?: boolean;
  disabled?: boolean;
  maxSelected?: number;
  /** Offer the typed string as a selectable value. Off by default. */
  allowLiteral?: boolean;
  /** Server-side search. Debouncing is the caller's job. */
  onSearchChange?: (query: string) => void;
  /** `options` is a clipped set rather than the whole domain. */
  truncated?: boolean;
  /** Size of the whole domain. Rendered with `truncated` as "showing N of M". */
  totalCount?: number;
  emptyMessage?: string;
  showSelectAll?: boolean;
  description?: string;
  errorMessage?: string;
  isRequired?: boolean;
  /** Fires when the search input loses focus. Form adapters use this to mark the field touched. */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** Ref to the search input, so callers (and `setFocus`) can focus the control. */
  inputRef?: React.Ref<HTMLInputElement>;
  /**
   * Render the label to assistive technology only. For a control embedded in a
   * surface that already names the field — a filter panel carrying the field name
   * as its section heading — a visible label just repeats it. The label stays in
   * the DOM and stays associated, because it is the combobox's accessible name;
   * only its presentation changes. Never a way to omit `label`.
   */
  labelHidden?: boolean;
  className?: string;
  id?: string;
}

type Row = { kind: "option"; option: MultiSelectOption } | { kind: "literal"; value: string };

const rowKey = (row: Row): string => (row.kind === "literal" ? `literal:${row.value}` : `option:${row.option.value}`);

const rowValue = (row: Row): string => (row.kind === "literal" ? row.value : row.option.value);

const rowLabel = (row: Row): string => (row.kind === "literal" ? row.value : row.option.label);

const iconSizeMap: Record<Size, string> = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6",
  xl: "h-7 w-7",
};

const inputPaddingLeftMap: Record<Size, string> = {
  sm: "pl-8",
  md: "pl-10",
  lg: "pl-11",
  xl: "pl-13",
};

const chipSizeMap: Record<Size, "xs" | Size> = {
  sm: "xs",
  md: "xs",
  lg: "sm",
  xl: "sm",
};

/**
 * MultiSelect
 *
 * A combobox whose popup is a multi-select listbox (W3C ARIA APG): a search
 * input narrows a checkbox list, and current selections live in their own
 * pinned region above it.
 */
export function MultiSelect({
  options,
  value,
  defaultValue,
  onChange,
  label,
  placeholder,
  size = "md",
  loading = false,
  disabled = false,
  maxSelected,
  allowLiteral = false,
  onSearchChange,
  truncated = false,
  totalCount,
  emptyMessage,
  showSelectAll = false,
  description,
  errorMessage,
  isRequired = false,
  onBlur,
  inputRef: forwardedInputRef,
  labelHidden = false,
  className,
  id,
}: MultiSelectProps) {
  const generatedId = useId();
  const baseId = id ?? `multiselect-${generatedId}`;
  const inputId = `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;

  const inputRef = useRef<HTMLInputElement>(null);
  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof forwardedInputRef === "function") forwardedInputRef(node);
      else if (forwardedInputRef) (forwardedInputRef as React.RefObject<HTMLInputElement | null>).current = node;
    },
    [forwardedInputRef]
  );
  const { contains } = useFilter({ sensitivity: "base" });
  const { isFocused, handleFocus, handleBlur } = useInputFocus();

  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(true);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const [uncontrolled, setUncontrolled] = useState<string[]>(() => [...(defaultValue ?? [])]);

  const selected = useMemo(() => (value ? [...value] : uncontrolled), [value, uncontrolled]);

  const filtered = useMemo(() => filterMultiSelectOptions(options, query, contains), [options, query, contains]);

  const offersLiteral = allowLiteral && !loading && shouldOfferLiteralOption({ query, options, selected });

  const rows = useMemo<Row[]>(() => {
    const optionRows: Row[] = filtered.map((option) => ({ kind: "option", option }));
    if (!offersLiteral) return optionRows;
    return [{ kind: "literal", value: query.trim() }, ...optionRows];
  }, [filtered, offersLiteral, query]);

  const selectionEntries = useMemo(() => multiSelectSelectionEntries(selected, options), [selected, options]);

  const atLimit = maxSelected !== undefined && selected.length >= maxSelected;

  const commit = useCallback(
    (next: string[], message: string) => {
      if (!value) setUncontrolled(next);
      setAnnouncement(`${message} ${next.length} selected.`);
      onChange(next);
    },
    [onChange, value]
  );

  const toggleValue = useCallback(
    (targetValue: string, displayLabel: string) => {
      if (disabled) return;
      if (selected.includes(targetValue)) {
        commit(
          selected.filter((candidate) => candidate !== targetValue),
          `${displayLabel} removed.`
        );
        return;
      }
      if (atLimit) return;
      commit([...selected, targetValue], `${displayLabel} selected.`);
    },
    [atLimit, commit, disabled, selected]
  );

  const activateRow = useCallback(
    (row: Row) => {
      if (row.kind === "option" && row.option.disabled) return;
      toggleValue(rowValue(row), rowLabel(row));
    },
    [toggleValue]
  );

  const removeValues = (values: readonly string[]) => {
    if (disabled || values.length === 0) return;
    const removing = new Set(values);
    const labels = values.map((removed) => selectionEntries.find((entry) => entry.value === removed)?.label ?? removed);
    commit(
      selected.filter((candidate) => !removing.has(candidate)),
      `${labels.join(", ")} removed.`
    );
  };

  const handleQueryChange = (next: string) => {
    setQuery(next);
    setActiveIndex(null);
    setIsOpen(true);
    onSearchChange?.(next);
  };

  const moveActive = (delta: number) => {
    if (rows.length === 0) {
      setActiveIndex(null);
      return;
    }
    setActiveIndex((current) => {
      if (current === null) return delta > 0 ? 0 : rows.length - 1;
      return (current + delta + rows.length) % rows.length;
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    const activeRow = activeIndex === null ? undefined : rows[activeIndex];

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setIsOpen(true);
        moveActive(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        setIsOpen(true);
        moveActive(-1);
        break;
      case "Home":
        if (rows.length > 0) {
          event.preventDefault();
          setIsOpen(true);
          setActiveIndex(0);
        }
        break;
      case "End":
        if (rows.length > 0) {
          event.preventDefault();
          setIsOpen(true);
          setActiveIndex(rows.length - 1);
        }
        break;
      case "Enter":
        if (activeRow) {
          event.preventDefault();
          activateRow(activeRow);
        }
        break;
      case " ":
        // Space toggles only while the query is empty; otherwise it has to stay a
        // literal space so multi-word queries remain typable.
        if (query === "" && activeRow) {
          event.preventDefault();
          activateRow(activeRow);
        }
        break;
      case "Escape":
        event.preventDefault();
        if (query !== "") {
          handleQueryChange("");
        } else {
          setIsOpen(false);
          setActiveIndex(null);
        }
        break;
      case "Backspace":
        if (query === "" && selected.length > 0) {
          event.preventDefault();
          removeValues([selected[selected.length - 1] as string]);
        }
        break;
      default:
        break;
    }
  };

  // What "select all" would actually add: matching, not disabled, not already
  // selected — and clipped to the remaining capacity under `maxSelected`. The
  // button label reports this number rather than the matching total, so a
  // capped press can't read as "the whole filtered set applied".
  const addableMatching = filtered
    .filter((option) => !option.disabled && !selected.includes(option.value))
    .map((option) => option.value);
  const remainingCapacity =
    maxSelected === undefined ? addableMatching.length : Math.max(0, maxSelected - selected.length);
  const willAdd = Math.min(addableMatching.length, remainingCapacity);
  const selectAllLabel =
    willAdd === addableMatching.length
      ? `Select all ${addableMatching.length} matching`
      : `Select ${willAdd} of ${addableMatching.length} matching`;

  const handleSelectAllMatching = () => {
    if (disabled || willAdd === 0) return;
    const added = addableMatching.slice(0, willAdd);
    commit([...selected, ...added], `${added.length} matching options selected.`);
  };

  const statusKind: "loading" | "empty" | "no-match" | null = loading
    ? "loading"
    : options.length === 0
      ? "empty"
      : filtered.length === 0 && query.trim() !== ""
        ? "no-match"
        : null;

  const textSizeClass = controlStyles[size].text;

  return (
    <div className={twMerge("flex w-full flex-col gap-1", className)} data-testid="multiselect">
      <Label size={size} htmlFor={inputId} isRequired={isRequired} {...(labelHidden ? { className: "sr-only" } : {})}>
        {label}
      </Label>

      <InputWrapper>
        <div className="pointer-events-none absolute left-3 top-1/2 z-10 flex -translate-y-1/2 items-center">
          {loading ? (
            <Loader size={16} color="text-text-secondary" />
          ) : (
            <Icon name="MagnifyingGlass" className={twMerge(iconSizeMap[size], "text-text-secondary")} />
          )}
        </div>
        <input
          ref={setInputRef}
          id={inputId}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={
            isOpen && activeIndex !== null && rows[activeIndex] ? `${baseId}-row-${activeIndex}` : undefined
          }
          aria-required={isRequired || undefined}
          aria-invalid={errorMessage ? true : undefined}
          value={query}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(event) => handleQueryChange(event.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={(event) => {
            setIsOpen(true);
            handleFocus(event);
          }}
          onBlur={(event) => {
            handleBlur(event);
            onBlur?.(event);
          }}
          className={getInputBaseStyles({
            isDisabled: disabled,
            isFocused,
            isInvalid: !!errorMessage,
            size,
            className: twMerge(inputPaddingLeftMap[size], "pr-3"),
          })}
        />
      </InputWrapper>

      {selectionEntries.length > 0 && (
        <div
          data-testid="multiselect-selection-region"
          role="group"
          aria-label={`Selected ${label}`}
          className="flex flex-wrap gap-1.5 rounded-[var(--control-border-radius)] border border-border-default bg-background-muted p-2"
        >
          {selectionEntries.map((entry) => (
            <Chip
              key={entry.value}
              size={chipSizeMap[size]}
              variant="primary"
              isDisabled={disabled}
              removeLabel={`Remove ${entry.label}`}
              onRemove={() => removeValues([entry.value])}
              className="shrink-0"
            >
              {entry.isUnknown ? (
                <span
                  data-testid={`multiselect-selection-unknown-${entry.value}`}
                  title="Not in the current option list"
                >
                  {`${entry.label} (custom)`}
                </span>
              ) : (
                entry.label
              )}
            </Chip>
          ))}
        </div>
      )}

      {showSelectAll && (
        <div className="flex items-center gap-2">
          <Button variant="link" size={size} onPress={handleSelectAllMatching} isDisabled={disabled || willAdd === 0}>
            {selectAllLabel}
          </Button>
          <Button
            variant="link"
            size={size}
            onPress={() => removeValues(selected)}
            isDisabled={disabled || selected.length === 0}
          >
            Clear all
          </Button>
        </div>
      )}

      <div
        id={listboxId}
        role="listbox"
        aria-multiselectable="true"
        aria-label={`${label} options`}
        hidden={!isOpen}
        className={twMerge(
          textSizeClass,
          "max-h-[260px] overflow-y-auto rounded-[var(--control-border-radius)] border border-border-input bg-background-input"
        )}
      >
        {isOpen &&
          rows.map((row, index) => {
            const isSelected = selected.includes(rowValue(row));
            const isRowDisabled =
              disabled || (row.kind === "option" && row.option.disabled === true) || (!isSelected && atLimit);
            return (
              // Rows carry no key handler by design: per APG the combobox input keeps
              // DOM focus and drives the list through aria-activedescendant.
              <div
                key={rowKey(row)}
                id={`${baseId}-row-${index}`}
                role="option"
                aria-selected={isSelected}
                aria-disabled={isRowDisabled || undefined}
                data-value={rowValue(row)}
                data-literal={row.kind === "literal" ? "true" : undefined}
                data-active={activeIndex === index ? "true" : undefined}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  if (isRowDisabled) return;
                  activateRow(row);
                  inputRef.current?.focus();
                }}
                className={twMerge(
                  "flex cursor-default items-center gap-2 px-3",
                  controlStyles[size].paddingY,
                  "text-text-primary",
                  activeIndex === index && "bg-background-hover",
                  isSelected && "bg-background-selected font-medium",
                  isRowDisabled && "text-text-disabled"
                )}
              >
                <CheckboxIndicator isSelected={isSelected} isDisabled={isRowDisabled} className="!w-4 !h-4" />
                <span className="flex-1 truncate">{rowLabel(row)}</span>
                {row.kind === "literal" && (
                  <span data-testid="multiselect-literal-badge" className="italic text-text-secondary">
                    Use as typed
                  </span>
                )}
                {row.kind === "option" && row.option.count !== undefined && (
                  <span data-testid={`multiselect-count-${row.option.value}`} className="text-text-secondary">
                    {row.option.count.toLocaleString()}
                  </span>
                )}
              </div>
            );
          })}
      </div>

      {statusKind === "loading" && (
        <div data-testid="multiselect-status-loading" data-status="loading">
          <Description size={size} className="flex items-center gap-2">
            <Loader size={14} color="text-text-secondary" />
            <span>Loading options…</span>
          </Description>
        </div>
      )}
      {statusKind === "empty" && (
        <div data-testid="multiselect-status-empty" data-status="empty">
          <Description size={size} className="flex items-center gap-2">
            <Icon name="Tray" className="h-4 w-4" />
            <span>{emptyMessage ?? "No options available"}</span>
          </Description>
        </div>
      )}
      {statusKind === "no-match" && (
        <div data-testid="multiselect-status-no-match" data-status="no-match">
          <Description size={size} className="flex items-center gap-2 text-feedback-warning-text">
            <Icon name="MagnifyingGlass" className="h-4 w-4" />
            <span>{`No options match “${query.trim()}”`}</span>
          </Description>
        </div>
      )}

      {truncated && (
        <div data-testid="multiselect-truncation">
          <Description size={size}>
            {totalCount === undefined
              ? "Showing a partial list — refine your search to see more"
              : `Showing ${options.length} of ${totalCount} — refine your search to see more`}
          </Description>
        </div>
      )}

      {maxSelected !== undefined && (
        <div data-testid="multiselect-limit">
          <Description size={size}>
            {atLimit
              ? `${selected.length} of ${maxSelected} selected — limit reached, remove one to choose another`
              : `${selected.length} of ${maxSelected} selected`}
          </Description>
        </div>
      )}

      {description && <Description size={size}>{description}</Description>}
      {errorMessage && <FieldError size={size}>{errorMessage}</FieldError>}

      <div data-testid="multiselect-live-region" role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </div>
  );
}
